import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * NewsletterSignup - inline subscription card for /relo-news.
 *
 * Captures email → POST /api/news/subscribe → Resend welcome email.
 * Calm, premium tone - Deep Ocean canvas with Golden Hour accent.
 */
const NewsletterSignup = ({ source = 'relo-news-grid' }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === 'loading') return;
    setStatus('loading');
    setMessage('');
    try {
      const r = await fetch(`${API_URL}/api/news/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: firstName, source }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setStatus('success');
        setMessage(
          data.already_subscribed
            ? "You're already on the list - see you Monday."
            : "Welcome aboard. We just sent you a confirmation."
        );
        setEmail('');
        setFirstName('');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Something went wrong. Try again?');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Try again?');
    }
  };

  return (
    <section
      className="relative overflow-hidden bg-[#0A1F2E] text-[#F9F6EF] py-16 md:py-20 my-24"
      data-testid="news-newsletter"
    >
      {/* Background grain + glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 15% 20%, rgba(232,168,48,0.18) 0%, transparent 45%), radial-gradient(circle at 85% 80%, rgba(44,168,160,0.14) 0%, transparent 50%)',
        }}
      />

      <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
        <p className="font-mono uppercase text-[10px] tracking-[0.22em] text-[#E8A830] mb-4">
          The Mundalo Dispatch · every other Monday
        </p>
        <h2 className="font-display italic text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-5">
          Get Relo News delivered.
        </h2>
        <p className="font-body text-[#F9F6EF]/75 text-base md:text-lg max-w-2xl mx-auto mb-10">
          One short email every other Monday - visas, housing, banking, and the people
          actually making the move. No spam, no upsell, no AI fluff.
        </p>

        {status === 'success' ? (
          <div
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#2CA8A0] text-[#0A1F2E] rounded-full font-mono uppercase text-[11px] tracking-[0.15em] font-semibold"
            data-testid="news-newsletter-success"
            role="status"
          >
            <CheckCircle2 className="w-4 h-4" />
            {message}
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="max-w-xl mx-auto flex flex-col sm:flex-row gap-3"
            data-testid="news-newsletter-form"
          >
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name (optional)"
              className="sm:w-1/3 px-4 py-3 bg-[#F9F6EF]/5 border border-[#F9F6EF]/20 text-[#F9F6EF] placeholder-[#F9F6EF]/40 rounded-full font-body focus:outline-none focus:border-[#E8A830] transition"
              data-testid="news-newsletter-firstname"
              autoComplete="given-name"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              className="flex-1 px-4 py-3 bg-[#F9F6EF]/5 border border-[#F9F6EF]/20 text-[#F9F6EF] placeholder-[#F9F6EF]/40 rounded-full font-body focus:outline-none focus:border-[#E8A830] transition"
              data-testid="news-newsletter-email"
              autoComplete="email"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E8A830] hover:brightness-95 disabled:opacity-50 text-[#0A1F2E] rounded-full font-mono uppercase text-[11px] tracking-[0.15em] font-semibold transition whitespace-nowrap"
              data-testid="news-newsletter-submit"
            >
              {status === 'loading' ? 'Subscribing…' : 'Subscribe'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {status === 'error' && (
          <div
            className="mt-4 inline-flex items-center gap-2 text-[#E8A830] text-sm font-body"
            data-testid="news-newsletter-error"
            role="alert"
          >
            <AlertCircle className="w-4 h-4" />
            {message}
          </div>
        )}

        <p className="mt-6 font-mono uppercase text-[9px] tracking-[0.2em] text-[#F9F6EF]/40">
          Read by 3,200+ movers. Unsubscribe anytime.
        </p>
      </div>
    </section>
  );
};

export default NewsletterSignup;
